// ProductsPage — the OMS product browser (/products).
//
// Source of truth is the STOREFRONT catalogue (storefront_products in OMS
// Postgres), not the Shopee API. Shopee is maintenance-only and its listing is a
// subset of the storefront's, so paging the Shopee API for 2,000 items on every
// visit bought nothing but latency and an auth gate. Everything here comes from
// GET /api/storefront/admin/products, which paginates and searches server-side.
//
// Shopee-only actions that used to live on this page are gone with it:
//   - "Add Product" (Shopee add_item)     → products are created by /list-product
//   - "Import to Shopify"                 → Shopify store closed 2026-07
//   - "Duplicate Product" (Shopee clone)  → /list-product
//   - List / Unlist (Shopee item_status)  → Inventory Health page (delist by SKU)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  Pagination,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  ListItemIcon,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AddShoppingCart as AddShoppingCartIcon,
  OpenInNew as OpenInNewIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import productLinksService from '../services/ProductLinksService';
import { useAuth } from '../contexts/AuthContext';
import { canViewProductLinks } from '../config/permissions';

// ---------------------------------------------------------------------------
// Types — mirror GET /api/storefront/admin/products
// ---------------------------------------------------------------------------
interface StorefrontImage {
  id: number | string;
  src: string;
  alt?: string;
  position?: number;
}

interface StorefrontVariant {
  id: number | string;
  title: string;
  price: string;
  compare_at_price: string | null;
  sku: string | null;
  available: boolean;
  inventory_quantity: number;
  option1: string | null;
  option2: string | null;
  option3: string | null;
}

interface StorefrontProduct {
  id: number | string;           // shopify_id
  product_id: number;            // storefront_products.id (internal PK)
  handle: string;
  title: string;
  description?: string;
  product_type?: string;
  vendor?: string;
  tags: string[];
  status: string;                // active | archived | draft
  created_at?: string;
  published_at?: string | null;
  images: StorefrontImage[];
  variants: StorefrontVariant[];
  options: { name: string }[];
  price_range: { min: string; max: string };
  compare_at_price_range: { min: string | null; max: string | null };
  base_sku: string | null;
  skus: string[];
  stock_total: number;
  metadata_key: string | null;
  tag: string;
  reference_image: string | null;
  is_mdoc: boolean;
  storefront_url: string | null;
  sales_count?: number;
}

const PAGE_SIZE = 48;

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'default'> = {
  active: 'success',
  draft: 'warning',
  archived: 'default',
};

const money = (v?: string | number | null) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '0'));
  return Number.isFinite(n) ? n.toFixed(0) : '0';
};

const priceLabel = (p: StorefrontProduct) => {
  const min = money(p.price_range?.min);
  const max = money(p.price_range?.max);
  return min === max ? `NT$${min}` : `NT$${min}–${max}`;
};

const variantDetails = (v: StorefrontVariant) => {
  const opts = [v.option1, v.option2, v.option3].filter(Boolean);
  return opts.length ? opts.join(' / ') : 'Default';
};

// Sheet cells arrive HTML-escaped (&amp; in a query string breaks the URL) —
// same decode the Receiving page does on the identical payload.
const decodeEntities = (s: string) => {
  if (!s) return '';
  const ta = document.createElement('textarea');
  ta.innerHTML = s;
  return ta.value.trim();
};

const ProductsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  // Held in a ref so the fetch callback does NOT depend on the snackbar
  // identity: a provider that hands back a fresh function each render would
  // otherwise re-create the callback, re-fire the effect and loop the fetch.
  const snack = useRef(enqueueSnackbar);
  snack.current = enqueueSnackbar;
  const { currentUser } = useAuth();

  // Sinsang sourcing links are competitively sensitive (they name the supplier),
  // so they are an explicit allowlist — Magnus + Adam — not "any OMS admin".
  // permissions.ts is the UI mirror; GET /api/product-links is the real gate
  // (productLinksAuth 403s everyone else).
  const canSeeSourceLinks =
    canViewProductLinks(currentUser?.id) && currentUser?.role !== 'counter';

  // --- list state ---------------------------------------------------------
  const [products, setProducts] = useState<StorefrontProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('newest');
  const [refreshKey, setRefreshKey] = useState(0);

  // --- dialogs ------------------------------------------------------------
  const [selectedProduct, setSelectedProduct] = useState<StorefrontProduct | null>(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [editingSku, setEditingSku] = useState<string>('');
  const [editingTag, setEditingTag] = useState<string>('');

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<string[]>([]);
  const [modalIndex, setModalIndex] = useState(0);

  // --- add to outlet ------------------------------------------------------
  const [outletOpen, setOutletOpen] = useState(false);
  const [outletProduct, setOutletProduct] = useState<StorefrontProduct | null>(null);
  const [outletVariantId, setOutletVariantId] = useState<string>('');
  const [outletPrice, setOutletPrice] = useState<string>('');
  const [outletQuantity, setOutletQuantity] = useState<number>(1);
  const [outletSubmitting, setOutletSubmitting] = useState(false);

  // --- Sinsang sourcing links (SKU → urls) --------------------------------
  const [sourceLinks, setSourceLinks] = useState<Map<string, string[]>>(new Map());

  useEffect(() => {
    if (!canSeeSourceLinks) {
      setSourceLinks(new Map());
      return undefined;
    }
    let cancelled = false;
    productLinksService
      .fetchProductLinks()
      .then((rows) => {
        if (cancelled) return;
        const map = new Map<string, string[]>();
        rows.forEach((row) => {
          if (!row?.id) return;
          const urls = Array.from(
            new Set([...(row.allLinks || []), row.link].map(decodeEntities).filter(Boolean))
          );
          if (urls.length) map.set(String(row.id).trim(), urls);
        });
        setSourceLinks(map);
      })
      // A 403 is an answer, not a failure: that account simply sees no links.
      .catch(() => { /* service already logged it; leave the map empty */ });
    return () => { cancelled = true; };
  }, [canSeeSourceLinks]);

  const sourceLinksFor = useCallback(
    (sku?: string | null): string[] => {
      if (!canSeeSourceLinks || !sku) return [];
      const exact = String(sku).trim();
      // Links are filed under the base SKU; a variant SKU ("3067-BK-M") falls
      // back to it, exactly as the Receiving page resolves them.
      const base = exact.split('-')[0].split('_')[0];
      return sourceLinks.get(exact) || sourceLinks.get(base) || [];
    },
    [canSeeSourceLinks, sourceLinks]
  );

  // Debounce the search box so typing a SKU doesn't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/storefront/admin/products', {
        params: { q: query, status, sort, page, limit: PAGE_SIZE },
      });
      setProducts(response.data.products || []);
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
    } catch (error: any) {
      console.error('Failed to load storefront products:', error);
      snack.current(
        error?.response?.data?.error || 'Failed to load storefront products',
        { variant: 'error' }
      );
      setProducts([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  }, [query, status, sort, page]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts, refreshKey]);

  // --- metadata (tag + reference image), keyed by SKU ---------------------
  // Reads come down with the product; writes go to the same endpoints the
  // orders page uses, so a tag set here shows up there too.
  const handleTagSave = async (product: StorefrontProduct, tagName: string) => {
    const key = product.metadata_key || product.base_sku;
    if (!key) {
      enqueueSnackbar('This product has no SKU to tag', { variant: 'warning' });
      return;
    }
    try {
      await api.post('/api/products/metadata', { productKey: key, tag: tagName });
      setProducts(prev =>
        prev.map(p => (p.product_id === product.product_id ? { ...p, tag: tagName } : p))
      );
      enqueueSnackbar('Tag updated', { variant: 'success' });
    } catch (error) {
      console.error('Failed to update tag:', error);
      enqueueSnackbar('Failed to update tag', { variant: 'error' });
    }
  };

  const handleUploadSubmit = () => {
    if (!selectedProduct || !imageFile) {
      enqueueSnackbar('Please select an image', { variant: 'error' });
      return;
    }
    const key = selectedProduct.metadata_key || selectedProduct.base_sku;
    if (!key) {
      enqueueSnackbar('This product has no SKU to attach a reference image to', { variant: 'warning' });
      return;
    }

    const product = selectedProduct;
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target?.result as string;
      img.onload = async () => {
        try {
          // Compress to <=600px/JPEG 0.6 — the column holds base64, so a raw
          // phone photo would be megabytes per row.
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const MAX = 600;
          let { width, height } = img;
          if (width > height && width > MAX) {
            height *= MAX / width;
            width = MAX;
          } else if (height >= width && height > MAX) {
            width *= MAX / height;
            height = MAX;
          }
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6);

          await api.post('/api/products/reference-image/base64', {
            productKey: key,
            referenceImage: compressedBase64,
          });

          setProducts(prev =>
            prev.map(p =>
              p.product_id === product.product_id
                ? { ...p, reference_image: compressedBase64 }
                : p
            )
          );
          enqueueSnackbar('Reference image uploaded', { variant: 'success' });
          setUploadDialogOpen(false);
          setImageFile(null);
        } catch (error) {
          console.error('Error uploading reference image:', error);
          enqueueSnackbar('Failed to upload reference image', { variant: 'error' });
        }
      };
    };
    reader.readAsDataURL(imageFile);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar('Image must be under 5MB', { variant: 'error' });
        return;
      }
      setImageFile(file);
    }
  };

  // --- image lightbox -----------------------------------------------------
  const openGallery = (product: StorefrontProduct, startIndex = 0) => {
    const imgs = (product.images || []).map(i => i.src).filter(Boolean);
    if (imgs.length === 0) return;
    setModalImages(imgs);
    setModalIndex(startIndex);
    setImageModalOpen(true);
  };
  const navGallery = (dir: number) =>
    setModalIndex(i => (i + dir + modalImages.length) % modalImages.length);

  // --- add to outlet ------------------------------------------------------
  const selectedOutletVariant = useMemo(
    () => outletProduct?.variants.find(v => String(v.id) === outletVariantId) || null,
    [outletProduct, outletVariantId]
  );

  const openOutletDialog = (product: StorefrontProduct) => {
    setOutletProduct(product);
    const first = product.variants.find(v => v.available) || product.variants[0] || null;
    setOutletVariantId(first ? String(first.id) : '');
    setOutletQuantity(1);
    setOutletOpen(true);
    setActionMenuAnchorEl(null);
  };

  // Outlet default is 40% off the live storefront price (unchanged from the
  // old page) — the operator can override before submitting.
  useEffect(() => {
    const base = parseFloat(selectedOutletVariant?.price || outletProduct?.price_range?.min || '0');
    if (base > 0) setOutletPrice((base * 0.6).toFixed(0));
  }, [selectedOutletVariant, outletProduct]);

  const handleSubmitToOutlet = async () => {
    if (!outletProduct || !selectedOutletVariant) return;
    const priceNum = parseFloat(outletPrice);
    const originalNum = parseFloat(selectedOutletVariant.price);
    if (!priceNum || priceNum <= 0 || priceNum >= originalNum) {
      enqueueSnackbar('Outlet price must be above zero and below the original price', { variant: 'warning' });
      return;
    }

    const details = variantDetails(selectedOutletVariant);
    const baseSku = selectedOutletVariant.sku
      || outletProduct.base_sku
      || `${outletProduct.handle}-${selectedOutletVariant.id}`;
    const images = (outletProduct.images || []).map((img, i) => ({
      id: img.id || i + 1,
      src: img.src,
      position: img.position || i + 1,
    }));

    setOutletSubmitting(true);
    try {
      // source:'storefront' is what the add endpoint branches on — it skips the
      // dead Shopify variant lookup and stores the payload as given.
      const response = await api.post('/api/outlet/add', {
        source: 'storefront',
        variant_id: selectedOutletVariant.id,
        sku: baseSku,
        title: outletProduct.title,
        variant_title: selectedOutletVariant.title || details,
        variant_details: details,
        display_sku: details !== 'Default' ? `${baseSku} (${details})` : baseSku,
        description: outletProduct.description || null,
        image_url: images[0]?.src || null,
        all_images: images.length ? JSON.stringify(images) : null,
        price: originalNum,
        discount_price: priceNum,
        available_quantity: outletQuantity,
      });
      const bin = response.data?.slot_number;
      enqueueSnackbar(
        `Added to outlet${bin ? ` — bin ${bin}` : ''}${response.data?.merged ? ' (merged into existing lot)' : ''}`,
        { variant: 'success' }
      );
      setOutletOpen(false);
    } catch (error: any) {
      console.error('Failed to add to outlet:', error);
      enqueueSnackbar(
        error?.response?.data?.message || error?.response?.data?.error || 'Failed to add to outlet',
        { variant: 'error' }
      );
    } finally {
      setOutletSubmitting(false);
    }
  };

  // -----------------------------------------------------------------------
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Storefront catalogue (metanoia.tw / mdoc.tw) — {total.toLocaleString()} products
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => setRefreshKey(k => k + 1)}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Refresh'}
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
          <TextField
            label="Search by SKU, title, handle or type"
            variant="outlined"
            size="small"
            fullWidth
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchInput ? (
                    <IconButton size="small" onClick={() => setSearchInput('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <SearchIcon fontSize="small" color="disabled" />
                  )}
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e: SelectChangeEvent) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Sort</InputLabel>
            <Select
              value={sort}
              label="Sort"
              onChange={(e: SelectChangeEvent) => {
                setSort(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="newest">Newest first</MenuItem>
              <MenuItem value="oldest">Oldest first</MenuItem>
              <MenuItem value="best-selling">Best selling</MenuItem>
              <MenuItem value="price-desc">Price high → low</MenuItem>
              <MenuItem value="price-asc">Price low → high</MenuItem>
              <MenuItem value="title">Title A → Z</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : products.length > 0 ? (
        <>
          <Grid container spacing={2}>
            {products.map((product) => {
              const cover = product.images?.[0]?.src;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product.product_id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ pb: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Tooltip title={product.title}>
                          <Typography gutterBottom variant="subtitle1" component="div" noWrap>
                            {product.title}
                          </Typography>
                        </Tooltip>
                        <IconButton
                          size="small"
                          aria-label={`product actions for ${product.base_sku || product.title}`}
                          onClick={(e) => {
                            setActionMenuAnchorEl(e.currentTarget);
                            setSelectedProduct(product);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Typography variant="body2" color="text.secondary" noWrap>
                        SKU: {product.base_sku || '—'}
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2">Stock: {product.stock_total}</Typography>
                        <Typography variant="body2">{priceLabel(product)}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          label={product.status}
                          color={STATUS_COLORS[product.status] || 'default'}
                        />
                        <Chip size="small" variant="outlined" label={`${product.variants.length} variants`} />
                        {product.is_mdoc && <Chip size="small" variant="outlined" label="MdoC" />}
                      </Box>
                    </CardContent>

                    <Box sx={{ position: 'relative', p: 1 }}>
                      {cover ? (
                        <>
                          <CardMedia
                            component="img"
                            height="160"
                            image={cover}
                            alt={product.title}
                            sx={{
                              objectFit: 'contain',
                              cursor: 'pointer',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'scale(1.03)' },
                            }}
                            onClick={() => openGallery(product, 0)}
                          />
                          {product.images.length > 1 && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, overflowX: 'auto' }}>
                              {product.images.slice(0, 5).map((img, index) => (
                                <Box
                                  key={img.id ?? index}
                                  component="img"
                                  src={img.src}
                                  alt={`${product.title} ${index + 1}`}
                                  onClick={() => openGallery(product, index)}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    objectFit: 'cover',
                                    borderRadius: 0.5,
                                    cursor: 'pointer',
                                    border: '1px solid #ddd',
                                    opacity: 0.85,
                                    '&:hover': { opacity: 1, borderColor: '#1976d2' },
                                  }}
                                />
                              ))}
                              {product.images.length > 5 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', px: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                  +{product.images.length - 5}
                                </Box>
                              )}
                            </Box>
                          )}
                        </>
                      ) : (
                        <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No image
                          </Typography>
                        </Box>
                      )}

                      {product.reference_image && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            width: 50,
                            height: 50,
                            border: '2px solid #f50057',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setModalImages([product.reference_image as string]);
                            setModalIndex(0);
                            setImageModalOpen(true);
                          }}
                        >
                          <img
                            src={product.reference_image}
                            alt="Reference"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ p: 1 }}>
                      {editingSku === String(product.product_id) ? (
                        <TextField
                          fullWidth
                          size="small"
                          label="Tag"
                          value={editingTag}
                          onChange={(e) => setEditingTag(e.target.value)}
                          onBlur={() => {
                            handleTagSave(product, editingTag);
                            setEditingSku('');
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleTagSave(product, editingTag);
                              setEditingSku('');
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <Chip
                          label={product.tag || 'Add tag'}
                          color={product.tag ? 'primary' : 'default'}
                          onClick={() => {
                            setEditingSku(String(product.product_id));
                            setEditingTag(product.tag || '');
                          }}
                          sx={{ width: '100%' }}
                        />
                      )}
                    </Box>

                    <CardActions sx={{ mt: 'auto' }}>
                      <Button
                        size="small"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => {
                          setSelectedProduct(product);
                          setUploadDialogOpen(true);
                        }}
                      >
                        Reference
                      </Button>
                      {product.storefront_url && (
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          href={product.storefront_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View live
                        </Button>
                      )}
                      {sourceLinksFor(product.base_sku).length > 0 && (
                        <Button
                          size="small"
                          color="secondary"
                          startIcon={<LinkIcon />}
                          href={sourceLinksFor(product.base_sku)[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Sinsang source link for ${product.base_sku}`}
                        >
                          Sinsang
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              {query && ` matching "${query}"`}
            </Typography>

            <Pagination
              count={pages}
              page={page}
              onChange={(_, value) => {
                setPage(value);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              color="primary"
            />

            <Typography variant="body2" color="text.secondary">
              Page {page} of {pages}
            </Typography>
          </Box>
        </>
      ) : (
        <Typography variant="body1" sx={{ p: 4, textAlign: 'center' }}>
          No products found.
        </Typography>
      )}

      {/* Actions menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={() => setActionMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setDetailsOpen(true);
            setActionMenuAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          View details
        </MenuItem>
        <MenuItem onClick={() => selectedProduct && openOutletDialog(selectedProduct)}>
          <ListItemIcon>
            <AddShoppingCartIcon fontSize="small" />
          </ListItemIcon>
          Add to outlet
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (selectedProduct?.storefront_url) {
              window.open(selectedProduct.storefront_url, '_blank', 'noopener');
            }
            setActionMenuAnchorEl(null);
          }}
          disabled={!selectedProduct?.storefront_url}
        >
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          Open on storefront
        </MenuItem>
        {sourceLinksFor(selectedProduct?.base_sku).map((url, index) => (
          <MenuItem
            key={url}
            onClick={() => {
              window.open(url, '_blank', 'noopener');
              setActionMenuAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <LinkIcon fontSize="small" />
            </ListItemIcon>
            {index === 0 ? 'Open on Sinsang' : `Sinsang alternate ${index}`}
          </MenuItem>
        ))}
      </Menu>

      {/* Product details */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        {selectedProduct && (
          <>
            <DialogTitle>{selectedProduct.title}</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                  {selectedProduct.images?.[0]?.src ? (
                    <Box
                      component="img"
                      src={selectedProduct.images[0].src}
                      alt={selectedProduct.title}
                      onClick={() => openGallery(selectedProduct, 0)}
                      sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', cursor: 'pointer' }}
                    />
                  ) : (
                    <Paper sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                      <Typography variant="body2" color="text.secondary">
                        No image
                      </Typography>
                    </Paper>
                  )}
                </Grid>

                <Grid item xs={12} md={7}>
                  <Typography variant="body1" gutterBottom>
                    <strong>SKU:</strong> {selectedProduct.base_sku || '—'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Status:</strong>{' '}
                    <Chip size="small" label={selectedProduct.status} color={STATUS_COLORS[selectedProduct.status] || 'default'} />
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Price:</strong> {priceLabel(selectedProduct)}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Stock:</strong> {selectedProduct.stock_total}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Type:</strong> {selectedProduct.product_type || '—'}
                    {selectedProduct.vendor ? ` · ${selectedProduct.vendor}` : ''}
                  </Typography>
                  {selectedProduct.tag && (
                    <Typography variant="body1" gutterBottom>
                      <strong>Tag:</strong> {selectedProduct.tag}
                    </Typography>
                  )}
                  {selectedProduct.storefront_url && (
                    <Typography variant="body1" gutterBottom>
                      <Link href={selectedProduct.storefront_url} target="_blank" rel="noopener noreferrer">
                        {selectedProduct.storefront_url}
                      </Link>
                    </Typography>
                  )}
                  {sourceLinksFor(selectedProduct.base_sku).map((url, index) => (
                    <Typography key={url} variant="body2" gutterBottom noWrap>
                      <strong>{index === 0 ? 'Sinsang:' : `Alternate ${index}:`}</strong>{' '}
                      <Link href={url} target="_blank" rel="noopener noreferrer">
                        {url}
                      </Link>
                    </Typography>
                  ))}
                  {selectedProduct.tags?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {selectedProduct.tags.map(t => (
                        <Chip key={t} size="small" variant="outlined" label={t} />
                      ))}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Variants
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Variant</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Stock</TableCell>
                        <TableCell align="right">Available</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProduct.variants.map(v => (
                        <TableRow key={v.id}>
                          <TableCell>{variantDetails(v)}</TableCell>
                          <TableCell>{v.sku || '—'}</TableCell>
                          <TableCell align="right">NT${money(v.price)}</TableCell>
                          <TableCell align="right">{v.inventory_quantity}</TableCell>
                          <TableCell align="right">
                            <Chip size="small" label={v.available ? 'yes' : 'no'} color={v.available ? 'success' : 'default'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>Close</Button>
              <Button variant="contained" onClick={() => openOutletDialog(selectedProduct)}>
                Add to outlet
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reference image upload */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload reference image</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" gutterBottom>
              Reference image for {selectedProduct?.title} (SKU {selectedProduct?.base_sku || '—'}).
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Resized to 600px JPEG before saving.
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" component="label" startIcon={<CloudUploadIcon />}>
                Select image
                <input type="file" hidden accept="image/jpeg,image/png" onChange={handleFileChange} />
              </Button>

              {imageFile && (
                <>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {imageFile.name} ({(imageFile.size / 1024).toFixed(0)} KB)
                  </Typography>
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img src={URL.createObjectURL(imageFile)} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200 }} />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUploadSubmit} variant="contained" disabled={!imageFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image lightbox */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { backgroundColor: 'black' } }}
      >
        <DialogContent sx={{ position: 'relative', p: 0, backgroundColor: 'black' }}>
          <IconButton
            onClick={() => setImageModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>

          {modalImages.length > 1 && (
            <>
              <IconButton
                onClick={() => navGallery(-1)}
                sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1 }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => navGallery(1)}
                sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1 }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', maxHeight: '80vh' }}>
            <img
              src={modalImages[modalIndex]}
              alt="Product"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          </Box>

          {modalImages.length > 1 && (
            <Typography
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                px: 2,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {modalIndex + 1} / {modalImages.length}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Add to outlet */}
      <Dialog open={outletOpen} onClose={() => !outletSubmitting && setOutletOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add to outlet</DialogTitle>
        <DialogContent dividers>
          {outletProduct && (
            <Box>
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Grid container spacing={2} alignItems="center">
                  {outletProduct.images?.[0]?.src && (
                    <Grid item xs={12} sm={4}>
                      <Box
                        component="img"
                        src={outletProduct.images[0].src}
                        alt={outletProduct.title}
                        sx={{ width: '100%', maxHeight: 150, objectFit: 'contain' }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={outletProduct.images?.[0]?.src ? 8 : 12}>
                    <Typography variant="subtitle1">{outletProduct.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      SKU: {outletProduct.base_sku || '—'} · stock {outletProduct.stock_total}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Variant</InputLabel>
                <Select
                  value={outletVariantId}
                  label="Variant"
                  onChange={(e: SelectChangeEvent) => setOutletVariantId(e.target.value)}
                >
                  {outletProduct.variants.map(v => (
                    <MenuItem key={v.id} value={String(v.id)}>
                      {variantDetails(v)} — NT${money(v.price)} · stock {v.inventory_quantity}
                      {v.available ? '' : ' (unavailable)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Original price"
                    fullWidth
                    disabled
                    value={money(selectedOutletVariant?.price || outletProduct.price_range.min)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Outlet price"
                    type="number"
                    fullWidth
                    required
                    value={outletPrice}
                    onChange={(e) => setOutletPrice(e.target.value)}
                    error={
                      !!outletPrice &&
                      (parseFloat(outletPrice) <= 0 ||
                        parseFloat(outletPrice) >= parseFloat(selectedOutletVariant?.price || '0'))
                    }
                    helperText={
                      !!outletPrice && parseFloat(outletPrice) >= parseFloat(selectedOutletVariant?.price || '0')
                        ? 'Must be below the original price'
                        : 'Defaults to 40% off'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Quantity"
                    type="number"
                    fullWidth
                    required
                    value={outletQuantity}
                    onChange={(e) => setOutletQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                The server assigns the outlet bin. For multi-item clearance runs use Outlet Manager → Add via storefront.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutletOpen(false)} disabled={outletSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitToOutlet}
            disabled={
              outletSubmitting ||
              !selectedOutletVariant ||
              !outletPrice ||
              parseFloat(outletPrice) <= 0 ||
              parseFloat(outletPrice) >= parseFloat(selectedOutletVariant?.price || '0')
            }
            startIcon={outletSubmitting ? <CircularProgress size={20} /> : <AddShoppingCartIcon />}
          >
            {outletSubmitting ? 'Adding…' : 'Add to outlet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsPage;
